import ReactFinalForm from 'components/form/ReactFinalForm';
import { IOption, ITransportActiveDocument } from 'interfaces';
import { IdentityField } from 'components/form/IdentityField';
import { useTranslation } from 'react-i18next';
import { TextFieldAdapter } from 'components/form/TextFieldAdapter';
import { useEffect, useState } from 'react';
import { AutocompleteAdapter } from 'components/form/AutocompleteAdapter';
import { stockQuantityLocationService, stockQuantityService } from 'services/api';
import toast from 'features/toast';

interface UpdateTransportActiveDocumentLineDialogFormParams {
  trasportActiveDocumentLine: any;
  ddt: ITransportActiveDocument;
  onSubmit: any;
}

// Function component to update contract line details
function UpdateTransportActiveDocumentLineDialog(
  params: UpdateTransportActiveDocumentLineDialogFormParams
) {
  const { t } = useTranslation();
  const { trasportActiveDocumentLine, ddt, onSubmit } = params;
  const [batchesOption, setBatchesOptions] = useState<IOption[]>([]);
  const [_, setSelectedBatch] = useState<IOption | null>(null);

  // Calculate the batches options
  useEffect(() => {
    const fetchBatchData = async () => {
      const params = {
        product__id: trasportActiveDocumentLine?.product ?? '',
        warehouse__id: ddt.warehouse_out ?? '',
        quantity__gt: 0
      };

      try {
        // Call both APIs concurrently
        const [stockQuantityRes, stockQuantityLocationRes] = await Promise.all([
          stockQuantityService.getUnloadStocks(1000, 0, params),
          stockQuantityLocationService.getUnloadStockLocation(1000, 0, params)
        ]);

        // Process the first API response
        const firstBatchData = stockQuantityRes.results.map((value) => {
          let description = value.product_batch;
          if (value.expiration_date) description += `; Scad: ${value.expiration_date}`;
          description += `; Qty: ${value.free_quantity}`;
          return {
            id: crypto.randomUUID(),
            code: description,
            product_batch: value.product_batch,
            expiration_date: value.expiration_date,
            qty: value.free_quantity
          };
        });

        // Process the second API response
        const secondBatchData = stockQuantityLocationRes.results.map((value) => {
          let description = value.product_batch;
          if (value.expiration_date) description += `; Scad: ${value.expiration_date}`;
          description += `; Qty: ${value.quantity}; ${value.location__code}`;
          return {
            id: crypto.randomUUID(),
            code: description,
            product_batch: value.product_batch,
            expiration_date: value.expiration_date,
            location: value.location__code,
            qty: value.quantity
          };
        });

        // Combine both results and update state
        setBatchesOptions([...firstBatchData, ...secondBatchData]);
      } catch (error) {
        toast.error(error);
      }
    };

    fetchBatchData();
  }, []);

  // Translation prefix for easier access to translation keys DA METTERE
  const translationPrefix = 'pages.transport-active-document-line.edit';

  // Function to handle form submission
  const onSubmitTrasportActiveDocumentLine = async (values) => {
    const params = {
      product_batch: values['product_batch'].product_batch ?? 'NO_BATCH',
      expiration_date: values['product_batch'].expiration_date,
      location: values['product_batch']?.location ?? '',
      notes: values['product_batch']?.location ?? '',
      quantity: values['quantity']
    };
    onSubmit(trasportActiveDocumentLine.id, params);
  };

  return (
    <div>
      {/* Form component to handle updating contract line */}
      <ReactFinalForm
        onSubmit={onSubmitTrasportActiveDocumentLine}
        formId="updateTransportActiveDocumentLine"
        hideControls>
        <div className="flex flex-wrap">
          <div className="w-1/2 p-4">
            <IdentityField
              name="product_batch"
              label={t(`${translationPrefix}.product_batch`)}
              component={AutocompleteAdapter}
              options={batchesOption}
              type="text"
              required
              setValue={setSelectedBatch}
              submitObject
              initValue={batchesOption.find(
                (b) => b.id === trasportActiveDocumentLine.product_batch
              )}
            />
          </div>
          <div className="w-1/2 p-4">
            <IdentityField
              component={TextFieldAdapter}
              name="quantity"
              label={t('global.quantity')}
              type="text"
              defaultValue={trasportActiveDocumentLine.quantity}
            />
          </div>
        </div>
      </ReactFinalForm>
    </div>
  );
}

export default UpdateTransportActiveDocumentLineDialog;
